/* eslint-disable no-param-reassign */
import { createSlice, SerializedError } from '@reduxjs/toolkit'
import { getDashboardByReportId } from 'features/liveReport/asyncThunks'
import { getDashboard, saveDashboard, deleteDashboard, copyDashboard } from './asyncThunks'
import { Dashboard } from '../../schemas/DashboardSchema'
import { OperatingStatus, Status } from '../types'

export interface DashboardSlice {
  dashboards: any[]
  isEditing: boolean
  rowHeight: number
  status: Status
  error: string | SerializedError | null | undefined
  statusCode: any
  dashboardData: Dashboard | null
  dashboardTempLayout: any
  operatingStatus?: OperatingStatus
}

export const initialState: Readonly<DashboardSlice> = {
  dashboards: [],
  isEditing: false,
  rowHeight: 112,
  status: 'idle',
  error: null,
  statusCode: null,
  dashboardData: null,
  dashboardTempLayout: null,
  operatingStatus: undefined
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setIsEditing: (state, action) => ({ ...state, isEditing: action.payload }),
    setDashboardTempLayout: (state, action) => {
      state.dashboardTempLayout = action.payload
    },
    clearDashboardError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(saveDashboard.pending, (state) => {
        // TODO: should be "loading" ?
        // @ts-ignore
        state.status = 'saving'
      })
      .addCase(saveDashboard.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.dashboards[0] = action.payload
      })
      .addCase(saveDashboard.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error
      })
      .addCase(deleteDashboard.pending, (state) => {
        state.operatingStatus = 'deleting'
      })
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.operatingStatus = undefined
        state.dashboards = state.dashboards.filter(
          (dashboard) => dashboard.dashboardId !== action.payload
        )
      })
      .addCase(deleteDashboard.rejected, (state, action) => {
        state.operatingStatus = undefined
        state.error = (action.payload as { error: string }).error
      })
      .addCase(copyDashboard.pending, (state) => {
        state.operatingStatus = 'copying'
      })
      .addCase(copyDashboard.fulfilled, (state) => {
        state.operatingStatus = undefined
      })
      .addCase(copyDashboard.rejected, (state, action) => {
        state.operatingStatus = undefined
        state.error = (action.payload as { error: string }).error
      })
      .addMatcher(
        (action) =>
          action.type === getDashboard.pending.type ||
          action.type === getDashboardByReportId.pending.type,
        (state) => {
          state.status = 'loading'
        }
      )
      .addMatcher(
        (action) =>
          action.type === getDashboard.fulfilled.type ||
          action.type === getDashboardByReportId.fulfilled.type,
        (state, action) => {
          state.status = 'succeeded'
          state.dashboardData = action.payload
        }
      )
      .addMatcher(
        (action) =>
          action.type === getDashboard.rejected.type ||
          action.type === getDashboardByReportId.rejected.type,
        (state, action) => {
          state.status = 'failed'
          if (action.payload) {
            state.error = action.payload.error
            state.statusCode = action.payload.status
          } else {
            state.error = action.error
          }
        }
      )
})

export const { setIsEditing, setDashboardTempLayout, clearDashboardError } = dashboardSlice.actions

export default dashboardSlice.reducer
